import { Box, IconButton, Stack, Typography } from '@mui/joy';
import { Plus, X } from '@phosphor-icons/react';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { Control, useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { useFormContext } from 'core/states';
import { ICorporatePart, ICorporatePartPriceListItem } from 'corporate/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { RemoveCorporatePartPriceButton } from './RemoveCorporatePartPriceButton';
import { ICorporatePartPriceItemForm, IUpsertCorporatePartPriceForm } from './types';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
  price?: ICorporatePartPriceListItem;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IUpsertCorporatePartPriceForm>) => void;
  onSubmit: (
    reset?: UseFormReset<IUpsertCorporatePartPriceForm>
  ) => (data: IUpsertCorporatePartPriceForm) => Promise<void>;
  onItemRemoved?: (itemId: number) => void;
  onRemoveSuccess?: () => void;
}

const newItemTemplate: Partial<ICorporatePartPriceItemForm> = {
  minQuantity: undefined,
  maxQuantity: undefined,
  marginReduction: undefined,
  discountIncrease: undefined,
};

const dateRowSx = { gap: 2 };
const itemsCardSx = {
  backgroundColor: 'background.level1',
  borderRadius: 'sm',
  padding: 1.5,
};
const itemsRowSx = { alignItems: 'flex-end', gap: 1 };
const decoratorSx = { color: 'text.tertiary', minWidth: '12px' };
const uomLabelSx = {
  flex: 0.5,
  color: 'text.tertiary',
  minWidth: '24px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const removeIconSx = { flex: 0.5, height: '32px' };
const drawerSlotAndSlotProps = {
  slotProps: { content: { sx: { width: 'clamp(560px, 55%, 720px)' } } },
};

export const UpsertCorporatePartPriceFormFields = ({
  corporatePart,
  price,
  isOpen,
  onClose,
  onSubmit,
  onItemRemoved,
  onRemoveSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors, getValues, watch } = useFormContext();

  const { fields, append, remove } = useFieldArray<
    IUpsertCorporatePartPriceForm,
    'priceItems',
    'fieldId'
  >({
    control: control as unknown as Control<IUpsertCorporatePartPriceForm>,
    name: 'priceItems',
    keyName: 'fieldId',
  });

  const uomLabel = corporatePart?.pricingUom;

  const startDate = watch('startDate') as Dayjs | null | undefined;
  const today = dayjs();
  const endDateMinDate = startDate ? dayjs(startDate).add(1, 'day') : today;

  const title = price ? t('titleEditPartPrice') : t('titleAddPartPrice');

  const removeButton = useMemo(() => {
    if (!price) {
      return null;
    }

    return (
      <RemoveCorporatePartPriceButton
        prices={[price]}
        buttonLabel={t('buttonLabelRemovePartPrice')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    );
  }, [price, onRemoveSuccess, t]);

  const handleAddItem = () => {
    const currentItems = (getValues('priceItems') ?? []) as ICorporatePartPriceItemForm[];
    const lastItem = currentItems[currentItems.length - 1];
    const lastMax = lastItem?.maxQuantity;
    const nextMinQuantity =
      lastMax != null && (lastMax as unknown) !== '' ? Number(lastMax) : undefined;

    append({
      ...newItemTemplate,
      minQuantity: nextMinQuantity,
    } as ICorporatePartPriceItemForm);
  };

  const handleRemoveItem = (index: number) => {
    const item = fields[index] as ICorporatePartPriceItemForm | undefined;

    if (item?.id) {
      onItemRemoved?.(item.id);
    }

    remove(index);
  };

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
      submitBtnLabel={price ? t('buttonLabelSave') : t('buttonLabelAddPartPrice')}
      drawerSlotAndSlotProps={drawerSlotAndSlotProps}
    >
      <Stack direction={Direction.ROW} sx={dateRowSx}>
        <FormField
          name="startDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelStartDate'),
            required: true,
            control,
            error: errors?.startDate,
            minDate: today,
          }}
        />
        <FormField
          name="endDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelEndDate'),
            control,
            error: errors?.endDate,
            minDate: endDateMinDate,
          }}
        />
      </Stack>

      <Stack sx={{ gap: 1 }}>
        <Typography level={Level.TITLE_SM}>{t('labelPricing')}</Typography>

        <Box sx={itemsCardSx}>
          <Stack sx={{ gap: 1 }}>
            {fields.map((field, index) => {
              const fieldErrors = errors?.priceItems?.[index];

              return (
                <Stack key={field.fieldId} direction={Direction.ROW} sx={itemsRowSx}>
                  <FormField
                    name={`priceItems.${index}.minQuantity`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('labelMinQty'),
                      required: true,
                      control,
                      error: fieldErrors?.minQuantity,
                      startDecorator: <Typography sx={decoratorSx}>≥</Typography>,
                    }}
                    wrapperStyle={{ flex: 2 }}
                  />
                  <FormField
                    name={`priceItems.${index}.maxQuantity`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('labelMaxQty'),
                      control,
                      error: fieldErrors?.maxQuantity,
                      startDecorator: <Typography sx={decoratorSx}>{'<'}</Typography>,
                    }}
                    wrapperStyle={{ flex: 2 }}
                  />
                  {uomLabel && (
                    <Typography level={Level.BODY_SM} sx={uomLabelSx}>
                      {uomLabel}
                    </Typography>
                  )}
                  <FormField
                    name={`priceItems.${index}.marginReduction`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('headerMarginReduction'),
                      placeholder: '%',
                      required: true,
                      control,
                      error: fieldErrors?.marginReduction,
                      slotProps: {
                        input: {
                          suffix: '%',
                        },
                      },
                    }}
                    wrapperStyle={{ flex: 3 }}
                  />
                  <FormField
                    name={`priceItems.${index}.discountIncrease`}
                    type={InputType.NUMBER_INPUT}
                    inputProps={{
                      label: t('headerDiscountIncrease'),
                      placeholder: '%',
                      required: true,
                      control,
                      error: fieldErrors?.discountIncrease,
                      slotProps: {
                        input: {
                          suffix: '%',
                        },
                      },
                    }}
                    wrapperStyle={{ flex: 3 }}
                  />
                  <IconButton
                    size={Size.SM}
                    color={Color.NEUTRAL}
                    variant={Variant.PLAIN}
                    onClick={() => handleRemoveItem(index)}
                    disabled={fields.length === 1}
                    sx={removeIconSx}
                    aria-label={t('buttonLabelRemove')}
                  >
                    <X />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Button
          text={t('buttonLabelAddQuantity')}
          color={Color.PRIMARY}
          variant={Variant.SOFT}
          startDecorator={<Plus />}
          onClick={handleAddItem}
          sx={{ alignSelf: 'flex-start' }}
        />
      </Stack>
    </Form>
  );
};
